import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import type { SessionService } from '@core/session.service';
import { UrlParam } from '@shared/enums/url-param.enum';
import {
  AttributeDescription,
  AttributeValue,
  CustomAttributeValue,
} from '@shared/interfaces/attribute-description.interface';
import { ArrayUtils } from '@shared/utils/array-utils';
import { StringUtils } from './string-utils';

export const FORM_ERROR_KEY = 'formError';

interface ErrorData {
  errors?: Record<string, string> | string;
  title?: string;
  toString?(): string;
}

export class FormUtils {
  public static markFormAsTouched(
    formGroup: UntypedFormGroup,
    onlySelf = false
  ): void {
    if (!formGroup?.controls) {
      return;
    }
    for (const key in formGroup.controls) {
      if (Object.hasOwn(formGroup.controls, key)) {
        const control: UntypedFormControl = formGroup.controls[
          key
        ] as UntypedFormControl;

        if (Object.keys(control).includes('controls')) {
          const formGroupChild: UntypedFormGroup = formGroup.controls[
            key
          ] as UntypedFormGroup;
          FormUtils.markFormAsTouched(formGroupChild, onlySelf);
        }

        control.markAsTouched({ onlySelf });
      }
    }
  }

  public static markControlAsTouch(
    form: UntypedFormGroup,
    controlName: string
  ): void {
    const control = form.controls[controlName];
    if (control) {
      control.markAllAsTouched();
      control.markAsDirty();
    }
  }

  public static removeError(control: AbstractControl, errorKey: string): void {
    if (!control?.errors) {
      return;
    }

    delete control.errors[errorKey];
    if (Object.keys(control.errors || {}).length === 0) {
      control.setErrors(null);
    }
  }

  public static isEqual<T>(value1: T, value2: T, primaryKey = null): boolean {
    if (!value1 && !value2) {
      return true;
    }

    if (value1 === value2) {
      return true;
    }

    if (value1 == null || value2 == null) {
      return false;
    }

    if (Array.isArray(value1) && Array.isArray(value2)) {
      return ArrayUtils.isEqual(value1, value2, primaryKey);
    }

    if (typeof value1 === 'object' && typeof value2 === 'object') {
      const isEqualUsingPrimaryKey =
        primaryKey && value1[primaryKey] && value2[primaryKey]
          ? value1[primaryKey] === value2[primaryKey]
          : false;

      return (
        isEqualUsingPrimaryKey ||
        JSON.stringify({ ...value1 }) === JSON.stringify({ ...value2 })
      );
    }

    return false;
  }

  public static isEqualSingleSelection(
    value1: string | CustomAttributeValue,
    value2: string | CustomAttributeValue,
    key = 'codeId'
  ): boolean {
    const compareValue1 = (value1 as CustomAttributeValue)[key] || value1;
    const compareValue2 = (value2 as CustomAttributeValue)[key] || value2;
    return FormUtils.isEqual(compareValue1, compareValue2);
  }

  public static getFieldOptions(
    attributeDescriptions: AttributeDescription[],
    key
  ): AttributeDescription | null {
    if (!attributeDescriptions || !key) {
      return null;
    }

    for (const item of attributeDescriptions) {
      if (StringUtils.equal(key, item.propertyName)) {
        return item;
      }
    }
    return null;
  }

  public static getFieldValue(
    attributeValues: AttributeValue[],
    key: string
  ): AttributeValue | null {
    if (!attributeValues || !key) {
      return null;
    }

    for (const item of attributeValues) {
      if (StringUtils.equal(key, item.propertyName)) {
        return item;
      }
    }
    return null;
  }

  public static hasContentInAnyControl(
    control: UntypedFormGroup,
    errorKey: string,
    controlNames: string[]
  ): Record<string, object> | null {
    if (!control || !controlNames) {
      return null;
    }

    const controls = controlNames.map(name => {
      return control.controls[name];
    });
    let valid = false;
    for (const item of controls) {
      if (!StringUtils.isEmptyHtmlContent(item?.value)) {
        valid = true;
        break;
      }
    }

    if (valid) {
      for (const item of controls) {
        FormUtils.removeError(item, errorKey);
      }
      return null;
    } else {
      for (const item of controls) {
        if (item) {
          item.setErrors({ [errorKey]: true });
        }
      }
      return { [errorKey]: { value: control?.value } };
    }
  }

  public static getErrorObject(data: unknown) {
    const errorMessage = {};
    const errorData = data as HttpErrorResponse;

    if (errorData.error) {
      this.processErrorData(errorData.error, errorMessage);
    } else if (data['isEmailDelivered'] === false && data['message']) {
      errorMessage[FORM_ERROR_KEY] = data['message'];
    }

    return errorMessage;
  }

  private static processErrorData(
    error: ErrorData,
    errorMessage: Record<string, string>
  ): void {
    const errors = error.errors;

    if (typeof errors === 'object') {
      this.processObjectErrors(errors, errorMessage);
    } else if (typeof errors === 'string') {
      errorMessage[FORM_ERROR_KEY] = '' + errors;
    } else if (error.title) {
      errorMessage[FORM_ERROR_KEY] = error.title;
    } else {
      this.processGenericError(error, errorMessage);
    }
  }

  private static processObjectErrors(
    errors: Record<string, string>,
    errorMessage: Record<string, string>
  ): void {
    for (const key of Object.keys(errors)) {
      if (key) {
        const message = errors[key];
        errorMessage[StringUtils.toLowerCaseFirstLetter(key)] = message;
      }
    }
  }

  private static processGenericError(
    error: ErrorData,
    errorMessage: Record<string, string>
  ): void {
    const message = navigator.onLine
      ? error.toString()
      : 'Network-Disconnected';

    errorMessage[FORM_ERROR_KEY] = message;
  }

  public static navigateTo(
    url: string,
    target: '_blank' | '_self' = '_self',
    origin = ''
  ): void {
    const fullUrl = origin ? origin + url : url;
    if (fullUrl) {
      window.open(
        StringUtils.propagateParamFromCurrentUrl(
          fullUrl,
          UrlParam.FilterTokenId
        ),
        target
      );
    }
  }

  public static navigateToWithLanguagePath(
    sessionService: SessionService,
    url: string
  ): void {
    this.navigateTo(sessionService.appendLanguagePath(url));
  }

  public static openWithLanguagePath(
    sessionService: SessionService,
    url: string,
    target: '_blank' | '_self' = '_self',
    origin = ''
  ): void {
    const urlWithLanguagePath = sessionService.appendLanguagePath(url);
    const fullUrl = origin ? origin + urlWithLanguagePath : urlWithLanguagePath;
    window.open(
      StringUtils.propagateParamFromCurrentUrl(fullUrl, UrlParam.FilterTokenId),
      target
    );
  }

  public static hasTrueValueControl(
    formControls: UntypedFormGroup,
    errorKey: string,
    controlNames: string[]
  ): Record<string, boolean> | null {
    if (!formControls || !controlNames) {
      return null;
    }

    const controls = controlNames.map(name => {
      return formControls.controls[name];
    });

    let isValid = true;
    for (const item of controls) {
      if (item.value) {
        if (item.hasError(errorKey)) {
          FormUtils.removeError(item, errorKey);
        }
      } else {
        item.setErrors({ [errorKey]: item.value });
        isValid = false;
      }
    }

    return isValid ? null : { [errorKey]: false };
  }

  public static lowercaseProperty(source: object): object {
    const result = {};

    for (const key of Object.keys(source)) {
      const lowerKey = StringUtils.toLowerCaseFirstLetter(key);
      result[lowerKey] = source[key];
    }

    return result;
  }

  public static customSearchFn(term: string, item: { value: string }) {
    return item.value.toLowerCase().startsWith(term.toLowerCase());
  }
}

export function integerOnly(errorKey: string): ValidatorFn {
  return regexValidator(errorKey, /^\d*$/);
}

export function regexValidator(
  errorKey: string,
  validRegex: RegExp
): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value;
    const valid = validRegex.test(control.value);

    return valid || !value ? null : { [errorKey]: { value: control.value } };
  };
}

export function notOnlyDigitsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value).trim();

    if (!value) return null;

    const onlyDigits = /^\d+$/;

    return onlyDigits.test(value) ? { onlyDigitsNotAllowed: true } : null;
  };
}
